import { useEffect, useRef, useCallback } from "react";

const TrackingEvents = {
  ASC_COMM_ENGAGEMENT: "asc_comm_engagement",
  ASC_COMM_SUBMISSION: "asc_comm_submission",
  ASC_FORM_ENGAGEMENT: "asc_form_engagement",
  ASC_FORM_SUBMISSION: "asc_form_submission",
  ASC_CTA_INTERACTION: "asc_cta_interaction",
};

const transformCategory = (itemCategory) => {
  if (itemCategory === "new" || itemCategory === "used") {
    return "sales";
  }
  return itemCategory;
};

const formatTrackingNumber = (trackingNumber) => {
  if (!trackingNumber) return [];
  return trackingNumber
    .split(/[,\s]+/)
    .map((id) => id.trim())
    .filter((id) => id);
};

const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const useAnalytics = (trackingNumber) => {
  const formEngagedWidgets = useRef(new Set());
  const ctaInteractedWidgets = useRef(new Set());
  const engagementTimeout = useRef(null);

  const emitEventToParent = useCallback(
    (eventData) => {
      const customEvent = {
        id: generateUUID(),
        type: "matador_analytics_event",
        timestamp: new Date().toISOString(),
        gaId: formatTrackingNumber(trackingNumber),
        ...eventData,
      };

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(customEvent, "*");
      }
    },
    [trackingNumber]
  );

  useEffect(() => {
    return () => {
      if (engagementTimeout.current) {
        clearTimeout(engagementTimeout.current);
      }
    };
  }, []);

  const sendEvent = useCallback(
    ({ name, widget, status, itemCategory }) => {
      const eventData = {
        name,
        comm_type: widget,
        event_owner: "matador",
        ...(status ? { status } : {}),
        ...(itemCategory ? { item_category: itemCategory } : {}),
      };

      emitEventToParent(eventData);
    },
    [emitEventToParent]
  );

  const sendCommEngagementEvent = useCallback(
    ({ widget, status = "start" }) => {
      if (engagementTimeout.current) {
        clearTimeout(engagementTimeout.current);
      }
      engagementTimeout.current = setTimeout(() => {
        sendEvent({ name: TrackingEvents.ASC_COMM_ENGAGEMENT, widget, status });
      }, 2000);
    },
    [sendEvent]
  );

  const sendCommSubmissionEvent = useCallback(
    ({ widget, status = "start", itemCategory }) => {
      const category = transformCategory(itemCategory);
      sendEvent({
        name: TrackingEvents.ASC_COMM_SUBMISSION,
        widget,
        status,
        itemCategory: category,
      });
    },
    [sendEvent]
  );

  const sendFormEngagementEvent = useCallback(
    ({ widget }) => {
      if (!formEngagedWidgets.current.has(widget)) {
        formEngagedWidgets.current.add(widget);
        sendEvent({ name: TrackingEvents.ASC_FORM_ENGAGEMENT, widget });
      }
    },
    [sendEvent]
  );

  const sendFormSubmissionEvent = useCallback(
    ({ widget }) => {
      sendEvent({ name: TrackingEvents.ASC_FORM_SUBMISSION, widget });
    },
    [sendEvent]
  );

  const sendCtaInteractionEvent = useCallback(
    ({ widget }) => {
      if (!ctaInteractedWidgets.current.has(widget)) {
        ctaInteractedWidgets.current.add(widget);
        sendEvent({ name: TrackingEvents.ASC_CTA_INTERACTION, widget });
      }
    },
    [sendEvent]
  );

  const d2cUtmTracking = (locationId) => {
    const d2cLocationIds = [
      "66423a2154c0270013a7d589",
      "6671c35e39b7f088eca4ef9c",
    ];
    if (d2cLocationIds.includes(locationId)) {
      window.parent.postMessage("userProvidedContactInfod2c", "*");
    }
  };

  return {
    sendCommEngagementEvent,
    sendCommSubmissionEvent,
    sendFormEngagementEvent,
    sendFormSubmissionEvent,
    sendCtaInteractionEvent,
    d2cUtmTracking,
  };
};

export default useAnalytics;
